import { getFlag } from '@mahawi/eshop-common/dist/src/get-flag';
import { getHumanReadableTaxValue } from '@mahawi/eshop-common/dist/src/get-human-readable-tax-value';
import { round } from '@mahawi/eshop-common/dist/src/modules/math/round';
import {
  EEventContextAction,
  EEventType,
  type IBasketProduct,
  type ICountry,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { getHead } from '../../head';
import OrderForm from '../components/basket/order-form';
import OrderTable from '../components/basket/order-table';
import { type IBasketState } from '../reducers/basket/types';
import { type ICountryState } from '../reducers/country/types';
import { eventSend } from '../reducers/event/reducer';
import { type IOrderState } from '../reducers/order/types';
import { setActiveCategory } from '../reducers/products/reducer';
import { type IUserState } from '../reducers/user/types';
import { type ITagManager } from '../types';

interface BasketContainerProps {
  Basket: IBasketState;
  dispatch: Dispatch;
  Country: ICountryState;
  Order: IOrderState;
  User: IUserState;
  TagManager?: ITagManager;
}

const BasketContainer = memo(
  ({
    Basket,
    dispatch,
    Country,
    Order,
    User,
    TagManager,
  }: BasketContainerProps): JSX.Element => {
    const { t } = useTranslation();

    const [totalGoodsWeight, setTotalGoodsWeight] = useState<number>(0);
    const [isModalOpened, setModalOpen] = useState(false);
    const [isForCompany, setIsForCompany] = useState<boolean>(false);
    const [companyHasPricesWithoutTax, setCompanyHasPricesWithoutTax] =
      useState<boolean>(false);

    const country: ICountry | undefined = useMemo(
      (): ICountry | undefined =>
        Country.countries.find(
          (c: { isoCode: string }): boolean =>
            c.isoCode === (User.countryType?.isoCode || 'CZE'),
        ),
      [Country.countries, User.countryType?.isoCode],
    );

    const countryCZE: ICountry | undefined = useMemo(
      (): ICountry | undefined =>
        Country.countries.find(
          (c: { isoCode: string }): boolean => c.isoCode === 'CZE',
        ),
      [Country.countries],
    );

    const vatRate: string = useMemo(
      (): string =>
        getHumanReadableTaxValue(
          country?.vatRate || countryCZE?.vatRate || 1.21,
        ),
      [country?.vatRate, countryCZE?.vatRate],
    );

    const flag: string = useMemo(
      (): string =>
        getFlag({ countryTypeIsoCode: User.countryType?.isoCode || 'CZE' }),
      [User.countryType?.isoCode],
    );

    const getItemsForTagManager = useCallback(() => {
      return Basket.products.map((product: IBasketProduct) => {
        const productNameEn: ITranslation | undefined = product.names.find(
          (n: ITranslation): boolean => n.code === 'EN',
        );
        const productPriceEur: ICurrencyPrice | undefined =
          product.currencyPrices.find(
            (c: ICurrencyPrice): boolean => c.isoCode === 'EUR',
          );
        const priceWithTax: ICountryCurrencyPrice | undefined =
          productPriceEur?.pricesWithTax.find(
            (c: ICountryCurrencyPrice): boolean =>
              c.isoCode === (User.countryType?.isoCode || 'CZE'),
          );

        return {
          item_id: product.uuid,
          item_name: productNameEn?.value || '---',
          price: priceWithTax?.price || 0,
          quantity: product.count,
          currency: 'EUR',
        };
      });
    }, [Basket.products, User.countryType?.isoCode]);

    const handleSetPrivatePerson = useCallback((): void => {
      setIsForCompany(false);
    }, []);

    const handleSetCompany = useCallback((): void => {
      setIsForCompany(true);
    }, []);

    const handleSetModalOpen = useCallback((isOpen: boolean): void => {
      setModalOpen(isOpen);
    }, []);

    const handleSetCompanyHasPricesWithoutTax = useCallback(
      (hasPrices: boolean): void => {
        setCompanyHasPricesWithoutTax(hasPrices);
      },
      [],
    );

    useEffect((): void => {
      const { title } = getHead(t, t('navigation.title.basket'));
      document.title = title;
    }, [t]);

    useEffect(() => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_BASKET,
          action: EEventContextAction.ENTER,
        }),
      );
      dispatch(
        setActiveCategory({
          category: {
            uuid: '',
          },
        }),
      );

      return (): void => {
        dispatch(
          eventSend({
            type: EEventType.PAGE_BASKET,
            action: EEventContextAction.LEAVE,
          }),
        );
      };
    }, [dispatch]);

    useEffect((): void => {
      const totalGoodsWeightUE: number = round(
        Basket.products.reduce(
          (acc: number, product: IBasketProduct): number =>
            acc + product.dimensions.weight * product.count || 0,
          0,
        ),
        4,
      );

      setTotalGoodsWeight(totalGoodsWeightUE);
    }, [Basket.products]);

    useEffect((): void => {
      if (Order.redirectLink !== '') {
        dispatch(
          eventSend({
            type: EEventType.ORDER_PAYMENT_REDIRECTION,
            action: EEventContextAction.REDIRECT,
          }),
        );

        window.location.href = Order.redirectLink;
      }
    }, [Order.redirectLink, dispatch]);

    const basketContent: ReactElement = useMemo((): ReactElement => {
      if (Basket.products.length === 0) {
        return <p>{t('basket.isEmpty')}</p>;
      }

      return (
        <>
          <OrderTable
            TagManager={TagManager}
            companyHasPricesWithoutTax={companyHasPricesWithoutTax}
            getItemsForTagManager={getItemsForTagManager}
          />

          <h2>{t('basket.totalGoodsWeight', { totalGoodsWeight })}</h2>
          <p>
            {isForCompany && companyHasPricesWithoutTax
              ? t('basket.allPricesIsWithoutTax')
              : t('product.countryVatRate', {
                  countryTypeIsoCode: User.countryType?.isoCode || 'CZE',
                  flag: flag,
                  vatRate: vatRate,
                })}
          </p>

          <button
            className={`order--button--is-for-company ${
              isForCompany ? '' : 'active'
            }`}
            type="button"
            onClick={handleSetPrivatePerson}
          >
            {t('basket.isForPrivatePerson')}
          </button>

          <button
            className={`order--button--is-for-company ${
              isForCompany ? 'active' : ''
            }`}
            type="button"
            onClick={handleSetCompany}
          >
            {t('basket.isForCompany')}
          </button>

          <OrderForm
            totalGoodsWeight={totalGoodsWeight}
            TagManager={TagManager}
            getItemsForTagManager={getItemsForTagManager}
            setModalOpen={handleSetModalOpen}
            companyHasPricesWithoutTax={companyHasPricesWithoutTax}
            setCompanyHasPricesWithoutTax={handleSetCompanyHasPricesWithoutTax}
            isForCompany={isForCompany}
          />
        </>
      );
    }, [
      Basket.products.length,
      companyHasPricesWithoutTax,
      flag,
      getItemsForTagManager,
      handleSetCompany,
      handleSetCompanyHasPricesWithoutTax,
      handleSetModalOpen,
      handleSetPrivatePerson,
      isForCompany,
      t,
      TagManager,
      totalGoodsWeight,
      User.countryType?.isoCode,
      vatRate,
    ]);

    return (
      <div className="basket container">
        <div className={`modal ${isModalOpened ? 'open' : 'close'}`}>
          {t('basket.contactingComgate')}
        </div>

        <h1>{t('basket.productsInBasket')}</h1>

        <div className="notification">
          <span>
            {t('basket.vacation', {
              dateFrom: '22.8.2025',
              dateTo: '31.8.2025',
              processedWhen: '1.9.2025',
            })}
          </span>
        </div>

        {basketContent}
      </div>
    );
  },
);

BasketContainer.displayName = 'BasketContainer';

const mapStateToProps = ({ Basket, Order, User, Country }) => ({
  Basket,
  Order,
  User,
  Country,
});

export default connect(mapStateToProps)(BasketContainer);
